/* ── SeasonContext.jsx ─────────────────────────────────────────────────────────────────── */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { supabase } from "../supabaseClient";

const SeasonContext = createContext();

export const useSeason = () => {
  const context = useContext(SeasonContext);
  if (!context) throw new Error("useSeason must be used within SeasonProvider");
  return context;
};

export const SeasonProvider = ({ children }) => {
  const [seasons, setSeasons] = useState([]);

  // The season where games/stats/playoffs are live
  const [activeSeason, setActiveSeason] = useState(null);

  // The season currently open for team registration (may differ from activeSeason)
  const [registrationSeason, setRegistrationSeason] = useState(null);

  // What the UI is currently viewing — defaults to activeSeason, can be changed for archives
  const [selectedSeason, setSelectedSeason] = useState(null);

  const [loading, setLoading] = useState(true);
  const [sponsors, setSponsors] = useState([]);

  const loadSeasons = useCallback(async () => {
    const { data, error } = await supabase
      .from("seasons")
      .select("*")
      .order("start_date", { ascending: false });

    if (error) {
      console.error("Failed to load seasons:", error);
      setLoading(false);
      return;
    }

    if (data) {
      setSeasons(data);

      // Active = where live gameplay happens (regular season or playoffs)
      const active =
        data.find((s) => s.status === "active" || s.status === "playoffs") ??
        null;

      // Registration = open for signups but not yet playing
      const registration =
        data.find((s) => s.status === "registration") ?? null;

      setActiveSeason(active);
      setRegistrationSeason(registration);

      // UI default: show the live season, not the registration one
      setSelectedSeason((prev) => prev ?? active);
    }

    setLoading(false);
  }, []);

  const loadSponsors = useCallback(async (seasonId) => {
    if (!seasonId) {
      setSponsors([]);
      return;
    }

    const { data, error } = await supabase
      .from("sponsors")
      .select("*")
      .eq("season_id", seasonId)
      .order("display_order", { ascending: true });

    if (!error) setSponsors(data || []);
  }, []);

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  // Load sponsors when selectedSeason changes
  useEffect(() => {
    if (selectedSeason?.id) {
      loadSponsors(selectedSeason.id);
    }
  }, [selectedSeason?.id, loadSponsors]);

  const value = {
    seasons,
    activeSeason, // use for all gameplay/stats/admin writes
    registrationSeason, // use for registration form inserts
    selectedSeason, // current UI view — use for standings/schedule display
    setSelectedSeason,
    loading,
    loadSeasons,
    sponsors,
    loadSponsors,
    // createNewSeason and archiveSeason removed — these now live in admin-actions
  };

  return (
    <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>
  );
};
